import { useMemo } from 'react';
import { TIER_FEATURES, TIER_INFO, MembershipTier, TierFeatureKey } from '../types/membership';
import { useUserProfile } from './useUserProfile';
import { getStreakMultiplier } from '../utils/xp';

export function useFeatureGating() {
  const { membershipProfile, isMembershipLoaded: loading } = useUserProfile();
  
  // Get tier from profile, default to 'starter' for logged-in users, 'guest' for anonymous
  // Check localStorage for dev tier override
  const tier: MembershipTier = useMemo(() => {
    // Check if dev auth mode is active via localStorage
    const devAuth = localStorage.getItem('flashread_dev_auth') === 'true';
    const devTier = localStorage.getItem('flashread_dev_tier') as MembershipTier;
    
    if (devAuth && devTier) {
      return devTier;
    }
    
    if (!membershipProfile) return 'guest';
    return (membershipProfile.membership_tier as MembershipTier) || 'guest';
  }, [membershipProfile]);

  const features = useMemo(() => {
    return TIER_FEATURES[tier];
  }, [tier]);

  const tierInfo = useMemo(() => {
    return TIER_INFO[tier];
  }, [tier]);

  /**
   * Check if a specific feature is accessible
   */
  const canAccess = (feature: TierFeatureKey): boolean => {
    const value = features[feature];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    // For 'basic'/'full' string values, treat as truthy
    return !!value;
  };

  /**
   * Check if user can access a specific feature tier
   */
  const canAccessTier = (requiredTier: MembershipTier): boolean => {
    const tierLevels: Record<MembershipTier, number> = {
      guest: 0,
      starter: 1,
      reader: 2,
      ultra: 3,
    };
    return tierLevels[tier] >= tierLevels[requiredTier];
  };

  /**
   * Check if user can add more books/slots
   */
  const checkLimit = (resource: 'books', current: number): {
    canAdd: boolean;
    remaining: number;
    isUnlimited: boolean;
  } => {
    const limit = features.maxBooks;
    const isUnlimited = limit === -1;
    
    if (isUnlimited) {
      return { canAdd: true, remaining: -1, isUnlimited: true };
    }
    
    const remaining = limit - current;
    return {
      canAdd: remaining > 0,
      remaining,
      isUnlimited: false,
    };
  };

  /**
   * Check if user can upload .txt files
   */
  const canUploadTxt = (): boolean => {
    return features.canUploadTxt;
  };

  /**
   * Check if user can upload .epub files
   */
  const canUploadEpub = (): boolean => {
    return features.canUploadEpub;
  };

  /**
   * Get the upload word limit (-1 = unlimited)
   */
  const getUploadWordLimit = (): number => {
    return features.uploadWordLimit;
  };

  /**
   * Check if user has analytics access
   */
  const hasAnalytics = (): boolean => {
    return features.hasAnalytics;
  };

  /**
   * Check if user has XP system enabled
   */
  const hasXP = (): boolean => {
    return features.xpEnabled;
  };

  /**
   * Check if user has daily challenges
   */
  const hasDailyChallenges = (): boolean => {
    return features.hasDailyChallenges;
  };

  /**
   * Check if user has progress persistence
   */
  const hasProgressPersistence = (): boolean => {
    return features.hasProgressPersistence;
  };

  /**
   * Check if user can control reading speed manually
   */
  const canControlSpeed = (): boolean => {
    return features.readerCanControlSpeed;
  };

  /**
   * Check if user can navigate between sections/chapters
   */
  const canNavigateSections = (): boolean => {
    return features.readerCanNavigateSections;
  };

  /**
   * Check if user can skip forward/backward in time
   */
  const canSkipTime = (): boolean => {
    return features.readerCanSkipTime;
  };

  /**
   * Check if user can use keyboard shortcuts
   */
  const canUseKeyboardShortcuts = (): boolean => {
    return features.readerCanUseKeyboardShortcuts;
  };

  /**
   * Check if user is forced to use auto-ramp (guest mode)
   */
  const isForcedRamp = (): boolean => {
    return features.readerForcedRamp;
  };

  /**
   * Get remaining trial sessions (-1 means unlimited)
   */
  const getTrialSessionsRemaining = (): number => {
    return features.trialSessions;
  };

  /**
   * Get remaining free stories for guest users (-1 means unlimited for paid tiers)
   */
  const getFreeStoriesRemaining = (): number => {
    if (tier !== 'guest') return -1; // unlimited for paid tiers
    // Backward compatibility: check both old and new keys
    const oldKey = parseInt(localStorage.getItem('flashread_guest_sessions') || '0', 10);
    const newKey = parseInt(localStorage.getItem('flashread_free_stories_used') || '0', 10);
    const used = Math.max(oldKey, newKey);
    return Math.max(0, 3 - used);
  };

  /**
   * Get the streak multiplier based on current streak
   */
  const getStreakMultiplierValue = (): number => {
    const streak = membershipProfile?.current_streak || 0;
    const baseMultiplier = getStreakMultiplier(streak);
    const bonus = features.streakMultiplierBonus || 0;
    return baseMultiplier + bonus;
  };

  /**
   * Check if user has session history access
   */
  const hasSessionHistory = (): boolean => {
    return features.hasSessionHistory;
  };

  /**
   * Check if user has cloud sync access
   */
  const hasCloudSync = (): boolean => {
    return features.hasCloudSync;
  };

  /**
   * Check if user has history export access
   */
  const hasHistoryExport = (): boolean => {
    return features.hasHistoryExport;
  };

  /**
   * Check if user has streak freezes available
   */
  const hasStreakFreezes = (): boolean => {
    return features.hasStreakFreezes;
  };

  /**
   * Get the streak multiplier bonus
   */
  const getStreakMultiplierBonus = (): number => {
    return features.streakMultiplierBonus || 0;
  };

  /**
   * Check if streak is at risk (missed yesterday)
   */
  const isStreakAtRisk = (): boolean => {
    if (!membershipProfile?.last_active_date) return false;
    
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    
    return membershipProfile.last_active_date !== today && membershipProfile.last_active_date !== yesterday;
  };

  return {
    tier,
    tierInfo,
    features,
    loading,
    canAccess,
    canAccessTier,
    checkLimit,
    canUploadTxt,
    canUploadEpub,
    getUploadWordLimit,
    hasAnalytics,
    hasXP,
    hasDailyChallenges,
    hasProgressPersistence,
    hasSessionHistory,
    hasCloudSync,
    hasHistoryExport,
    hasStreakFreezes,
    getStreakMultiplier: getStreakMultiplierValue,
    getStreakMultiplierBonus,
    isStreakAtRisk,
    // Reader-specific features
    canControlSpeed,
    canNavigateSections,
    canSkipTime,
    canUseKeyboardShortcuts,
    isForcedRamp,
    getTrialSessionsRemaining,
    getFreeStoriesRemaining,
    hasCustomThemes: () => false,
  };
}
